import { BellRing, ChevronDown, Lock, Save, Smartphone } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { choosePhoneAddress, phoneUrlError } from '@orgus/protocol';
import { Badge, Button, ErrorState, Input, Pressable, QrCode, Segmented, Skeleton, cn, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { SETTINGS_SECTIONS } from '../../settings/registry.ts';
import { savedPhoneUrl, updateOnboarding, useOnboarding } from '../client.ts';
import { Callout, Code, CopyLine } from '../parts.tsx';

type Os = 'ios' | 'android';

const guessOs = (): Os => (/android/i.test(navigator.userAgent) ? 'android' : 'ios');

/** One setup step as a stepper row: the title always, the body only while it is the open step. */
function SetupStep({ n, title, open, onOpen, children }: { n: number; title: string; open: boolean; onOpen: () => void; children: ReactNode }) {
  return (
    <li className="flex flex-col">
      <Pressable aria-expanded={open} onClick={onOpen} className="group flex min-h-control items-center gap-3 rounded-control text-left">
        <span
          className={cn('flex size-5 shrink-0 items-center justify-center rounded-full text-caption font-medium ring-1 num', open ? 'bg-accent-soft text-accent ring-accent' : 'bg-raised text-fg-muted ring-border')}
          aria-hidden
        >
          {n}
        </span>
        <span className={cn('min-w-0 flex-1 text-ui font-medium', open ? 'text-fg' : 'text-fg-muted group-hover:text-fg')}>{title}</span>
        <ChevronDown className={cn('size-4 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast)', open && 'rotate-180')} aria-hidden />
      </Pressable>
      {open && <div className="ml-8 flex flex-col gap-2 pb-2 text-ui text-fg-muted">{children}</div>}
    </li>
  );
}

/** Phone push, the recommended channel in step 5: open the hub on the phone, install it as an app, turn on push. */
export function PhoneStep() {
  const navigate = useNavigate();
  const state = useOnboarding((s) => s.state);
  const reach = useOnboarding((s) => s.reach);
  const reachPhase = useOnboarding((s) => s.reachPhase);
  const notify = useOnboarding((s) => s.probes.notify);
  const github = useOnboarding((s) => s.probes.auth?.githubConfigured ?? false);
  const role = useOnboarding((s) => s.probes.auth?.me?.role ?? 'owner');
  const isAdmin = role === 'owner' || role === 'admin';
  const saved = savedPhoneUrl(state);
  const [draft, setDraft] = useState(saved ?? notify?.publicUrl ?? '');
  const [draftTouched, setDraftTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Why saving the address failed: shown under the field, never in a toast. */
  const [saveError, setSaveError] = useState<UiError | null>(null);
  const [os, setOs] = useState<Os>(guessOs);
  const hasNotifications = SETTINGS_SECTIONS.some((s) => s.id === 'notifications');

  useEffect(() => {
    if (!draftTouched) setDraft(saved ?? notify?.publicUrl ?? '');
  }, [saved, notify?.publicUrl, draftTouched]);

  const addr = choosePhoneAddress({ browserOrigin: window.location.origin, saved: saved ?? notify?.publicUrl ?? null, reach });
  const draftError = draft.trim() ? phoneUrlError(draft) : null;
  const port = reach?.port ?? (Number(window.location.port) || 4600);
  // Only say what is known: a typed address was never opened here, and push needs the notify module.
  const readyLabel = addr.source === 'saved' ? 'https address saved' : notify?.installed ? 'Ready for install and push' : 'Ready to install';
  // A stepper: the step to do now is open, the others are one line each and open on click.
  const [step, setStep] = useState(() => (!addr.ready ? 1 : notify?.phones.length ? 4 : 2));

  const save = async () => {
    setDraftTouched(true);
    if (draftError || busy) return;
    setBusy(true);
    setSaveError(null);
    try {
      await updateOnboarding({ op: 'phone-url', url: draft.trim() || null });
      setDraftTouched(false);
      toast.success(draft.trim() ? 'Address saved' : 'Address cleared', draft.trim() ? 'The QR code now opens it.' : undefined);
    } catch (e) {
      setSaveError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="flex shrink-0 flex-col items-center gap-2 md:w-52">
          {reachPhase === 'loading' && !reach && !addr.url ? (
            <Skeleton className="size-44" />
          ) : addr.url ? (
            <QrCode value={addr.url} label={`QR code that opens ${addr.url}`} className="size-44" muted={!addr.ready} />
          ) : (
            <div className="flex size-44 flex-col items-center justify-center gap-2 rounded-card border border-dashed border-border text-center text-caption text-fg-subtle" role="img" aria-label="No address a phone can open yet">
              <Lock className="size-5" aria-hidden />
              No address yet
            </div>
          )}
          {/* Without https the QR code is dimmed and step 1 says what to do: no second chip saying it. */}
          {addr.url ? (
            addr.ready && (
              <Badge tone="passed" dot>
                {readyLabel}
              </Badge>
            )
          ) : (
            <Badge tone="offline">Not reachable from a phone</Badge>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <p className="text-ui text-fg-muted">Scan with your phone's camera: approve in one tap, and get push when a session waits or a pipeline fails.</p>
          {addr.url && <CopyLine text={addr.url} label="Phone address" />}
          {addr.ready && <p className="text-caption text-fg-subtle">{addr.note}</p>}
        </div>
      </div>

      <ol className="flex flex-col gap-1" aria-label="Phone setup">
        <SetupStep n={1} title="Give the hub an https address" open={step === 1} onOpen={() => setStep(1)}>
          {!addr.ready && <p>Phones install the app and allow push only over https.</p>}
          <p>
            With Tailscale: run <Code>{`tailscale serve --bg ${port}`}</Code>, start the hub with <Code>ORGUS_ALLOWED_HOSTS=.ts.net</Code>, and save the https address it prints. A
            reverse proxy with a real certificate works too.
          </p>
          <div className="flex flex-wrap items-start gap-2">
            <Input
              wrapperClassName="w-full sm:w-auto sm:min-w-64 sm:flex-1"
              aria-label="The hub's https address"
              placeholder="https:// address"
              value={draft}
              onChange={(e) => {
                setDraftTouched(true);
                setDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save();
              }}
              disabled={!isAdmin}
              hint={isAdmin ? undefined : 'Only admins set this address: every phone QR code opens it.'}
              error={draftTouched ? (draftError ?? undefined) : undefined}
            />
            <Button icon={<Save />} loading={busy} disabled={!isAdmin || !draftTouched || (draft.trim() || null) === saved} onClick={save}>
              Save address
            </Button>
          </div>
          {saveError && <ErrorState variant="inline" error={saveError} onRetry={save} />}
        </SetupStep>
        <SetupStep n={2} title="Sign in on the phone" open={step === 2} onOpen={() => setStep(2)}>
          <p>
            {github ? 'Sign in with GitHub, or open' : 'Open'} a link from <Code>orgus token</Code> with the https address in place of <Code>{window.location.origin}</Code>.
          </p>
        </SetupStep>
        <SetupStep n={3} title="Install it as an app" open={step === 3} onOpen={() => setStep(3)}>
          <Segmented
            label="Phone"
            value={os}
            onChange={setOs}
            options={[
              { value: 'ios', label: 'iPhone and iPad' },
              { value: 'android', label: 'Android' },
            ]}
          />
          {os === 'ios' ? (
            <p>
              In Safari tap Share, then Add to Home Screen. Open Orgus from the Home Screen: iPhone only sends push to an installed app (iOS 16.4 or later).
            </p>
          ) : (
            <p>In Chrome tap the menu, then Install app (or Add to Home screen).</p>
          )}
        </SetupStep>
        <SetupStep n={4} title="Turn on push" open={step === 4} onOpen={() => setStep(4)}>
          <p>In the app open Settings, Notifications, and turn on push for this device.</p>
          {notify?.installed ? (
            notify.phones.length ? (
              <Callout tone="ok" title={`${notify.phones.length === 1 ? 'A phone receives' : `${notify.phones.length} phones receive`} push`}>
                {notify.phones.map((p) => p.device).join(', ')}.
              </Callout>
            ) : (
              <p className="flex items-center gap-2 text-caption text-fg-subtle" role="status" aria-live="polite">
                <BellRing className="size-3.5" aria-hidden />
                No phone receives push yet. This updates by itself when one does.
              </p>
            )
          ) : null}
          {hasNotifications && (
            <div>
              <Button size="sm" icon={<Smartphone />} onClick={() => navigate('/settings/notifications')}>
                Notification settings
              </Button>
            </div>
          )}
        </SetupStep>
      </ol>
    </div>
  );
}
